import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/sonner";
import { api, getToken, clearToken } from "@/lib/api";
import { Sidebar } from "@/components/Sidebar";
import Connect from "@/pages/Connect";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Briefing from "@/pages/Briefing";
import Inbox from "@/pages/Inbox";
import Schedule from "@/pages/Schedule";
import PendingActions from "@/pages/PendingActions";
import ActivityLog from "@/pages/ActivityLog";
import { Sparkle } from "@phosphor-icons/react";

function Loading({ label }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-background">
      <div className="h-11 w-11 rounded-2xl bg-slate-900 flex items-center justify-center aria-thinking">
        <Sparkle weight="fill" className="text-white" size={24} />
      </div>
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  );
}

function Shell({ me, refetchMe }) {
  const navigate = useNavigate();
  const { data: actions } = useQuery({ queryKey: ["actions"], queryFn: api.actions });
  const pending = actions?.length || 0;
  const logout = () => { clearToken(); navigate("/login"); };
  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar user={me.user} pending={pending} onLogout={logout} />
      <main className="flex-1 min-w-0 h-screen overflow-y-auto aria-scroll">
        <Routes>
          <Route path="/" element={<Briefing user={me.user} />} />
          <Route path="/inbox" element={<Inbox />} />
          <Route path="/calendar" element={<Schedule />} />
          <Route path="/actions" element={<PendingActions />} />
          <Route path="/activity" element={<ActivityLog />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

function AppGate() {
  const location = useLocation();
  const hasToken = !!getToken();
  const { data: me, isLoading, refetch } = useQuery({
    queryKey: ["me"],
    queryFn: api.me,
    enabled: hasToken,
    retry: false,
  });

  if (!hasToken) return <Navigate to="/login" replace />;
  if (isLoading || !me) return <Loading label="Waking up Auppo…" />;

  const connected = me.settings?.google_connected;
  if (!connected) {
    if (location.pathname !== "/connect") return <Navigate to="/connect" replace />;
    return <Connect onConnected={refetch} />;
  }
  if (location.pathname === "/connect") return <Navigate to="/" replace />;
  return <Shell me={me} refetchMe={refetch} />;
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/auth/google" element={<AuthCallback />} />
        <Route path="*" element={<AppGate />} />
      </Routes>
      <Toaster position="top-right" richColors />
    </BrowserRouter>
  );
}

export default App;
